import { useMemo, type CSSProperties } from 'react'
import { motion } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useAudioInputLevel } from '@renderer/hooks/audio/use-audio-input-level'
import { useNoAudio } from '@renderer/hooks/audio/use-no-audio'

export const AudioActivity = ({ small, overrideInputLevel }: { small?: boolean; overrideInputLevel?: number }) => {
  const noAudio = useNoAudio()
  const inputLevel = useAudioInputLevel()

  const level = useMemo(() => {
    if (overrideInputLevel !== undefined) {
      return overrideInputLevel || 0
    }
    return inputLevel || 0
  }, [inputLevel, overrideInputLevel])

  const hasAudio = useMemo(() => {
    if (overrideInputLevel) {
      return true
    }
    return !noAudio
  }, [noAudio, overrideInputLevel])

  // When input level changes, adjust the audio levels for each line
  const audioLevels = useMemo(() => {
    const max = small ? 6 : 8
    const modifier = small ? 0.6 : 1
    return [
      Math.max(1, Math.min(level * 2 * modifier, max)),
      Math.max(1, Math.min(level * 5 * modifier, max)),
      Math.max(1, Math.min(level * 3 * modifier, max)),
    ]
  }, [level, small])

  return (
    <div className={'flex items-center gap-0.5'}>
      {audioLevels.map((waveLevel, index) => {
        const targetHeight = level > 0 ? waveLevel * 2 : 2
        const scaleY = targetHeight / 2 // Base height is 2px
        return (
          <motion.div
            key={`audio-level-${index}`}
            className={twMerge('h-[2px] w-[2px] origin-center rounded-[1px]', hasAudio ? 'bg-green' : 'bg-light-20')}
            style={transformStyle}
            animate={{
              scaleY: scaleY,
            }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />
        )
      })}
    </div>
  )
}

const transformStyle: CSSProperties = {
  transformOrigin: 'center center',
}
